import { useState } from 'react';
import { api } from '../../lib/api';
import { Button, ErrorText, Input, Modal } from '../ui';

const DURATIONS = [
  { h: 1, label: '1 hour' },
  { h: 4, label: '4 hours' },
  { h: 8, label: '8 hours' },
  { h: 24, label: '24 hours' },
  { h: 72, label: '3 days' },
  { h: 168, label: '1 week' },
  { h: 336, label: '2 weeks' },
];

export function CreatePollModal({
  channelId,
  onClose,
}: {
  channelId: string;
  onClose: () => void;
}) {
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState(['', '']);
  const [multi, setMulti] = useState(false);
  const [duration, setDuration] = useState(24);
  const [error, setError] = useState<unknown>(null);
  const filled = answers.map((a) => a.trim()).filter(Boolean);

  async function post() {
    try {
      await api('POST', `/channels/${channelId}/messages`, {
        poll: { question, answers: filled, allowMultiselect: multi, durationHours: duration },
      });
      onClose();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal title="Create a poll" onClose={onClose}>
      <div className="space-y-3">
        <Input
          label="Question"
          value={question}
          maxLength={300}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="What should we play tonight?"
        />
        <div className="space-y-2">
          <span className="block text-xs font-bold uppercase tracking-wide text-text-2">
            Answers
          </span>
          {answers.map((a, i) => (
            <div key={i} className="flex gap-2">
              <Input
                aria-label={`Answer ${i + 1}`}
                value={a}
                maxLength={55}
                placeholder={`Answer ${i + 1}`}
                onChange={(e) => setAnswers(answers.map((x, j) => (j === i ? e.target.value : x)))}
              />
              {answers.length > 1 && (
                <Button
                  variant="ghost"
                  title={`Remove answer ${i + 1}`}
                  onClick={() => setAnswers(answers.filter((_, j) => j !== i))}
                >
                  ✕
                </Button>
              )}
            </div>
          ))}
          {answers.length < 10 && (
            <Button variant="link" onClick={() => setAnswers([...answers, ''])}>
              + Add answer
            </Button>
          )}
        </div>
        <label className="block text-sm text-text-2">
          Duration
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="mt-1 block w-full rounded bg-bg-0 px-3 py-2 text-text-0"
          >
            {DURATIONS.map((d) => (
              <option key={d.h} value={d.h}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-text-1">
          <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} />
          Allow multiple answers
        </label>
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!question.trim() || filled.length === 0} onClick={post}>
            Post poll
          </Button>
        </div>
      </div>
    </Modal>
  );
}
